import { Table } from "antd";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { CREATE_jwel } from "../../Config/Config";
import PdfExcelPrint from "../Utilities/PdfExcelPrint";
import styles from "../Pages/shared/Inventory.module.css";
import InventoryPageHeader from "../Pages/shared/InventoryPageHeader";
import InventoryTable from "../Pages/shared/InventoryTable";
import { CalendarOutlined, ShoppingOutlined } from "@ant-design/icons";

/* ---------------- Shared card-view field maps (mobile) ---------------- */
const primaryFields = [
  { key: "billno", label: "Inv No" },
  { key: "itemname", label: "Item" },
  { key: "qty", label: "Qty", badge: "pieces" },
  { key: "amount", label: "Amount", badge: "netwt" },
];
const secondaryFields = [
  { key: "billdate", label: "Inv Date" },
  { key: "ITEMCODE", label: "Design Code" },
  { key: "rate", label: "MRP" },
  { key: "ACTRATE", label: "Cust.Price" },
];

const ProductWiseSale = () => {
  const [filteredData, setFilteredData] = useState([]);
  const [openingCash, setOpeningCash] = useState(0);
  const [dates, setDates] = useState([dayjs(), dayjs()]);
  const [tableVisible, setTableVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const tenantName = localStorage.getItem("tenantName");

  /* ── opening cash (kept as before) ── */
  useEffect(() => {
    if (dates[0] && dates[1]) {
      const fromDate = dates[0].format("YYYY-MM-DD");
      const saleCode = 1;

      axios
        .get(
          `${CREATE_jwel}/api/POSReports/GetCashBookOpenings?billDate=${fromDate}&saleCode=${saleCode}`,
          {
            headers: {
              tenantName: tenantName,
            },
          },
        )
        .then((response) => {
          const openingData = response.data[0];
          const openingCashValue = openingData.Column1 - openingData.Column2;
          setOpeningCash(openingCashValue);
        })
        .catch((error) => {
          console.error("Error fetching opening cash:", error);
        });
    }
  }, [dates]);

  /* ── fetch product wise sale for the selected date range ── */
  useEffect(() => {
    if (dates[0] && dates[1]) {
      const fromDate = dates[0].format("MM/DD/YYYY");
      const toDate = dates[1].format("MM/DD/YYYY");

      setLoading(true);
      setTableVisible(false);

      axios
        .get(
          `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhereandOrder?tableName=BILL_ITEMS_DET&where=BILLDATE>='${fromDate}'%20AND%20BILLDATE<='${toDate}'&order=BILLNO%2CSNO`,
          {
            headers: {
              tenantName: tenantName,
            },
          },
        )
        .then((response) => {
          const detailsData = Array.isArray(response.data) ? response.data : [];
          const calculatedData = detailsData.map((item, index) => {
            const itemType = String(item?.TYPE || "").trim().toUpperCase();
            const salesType = String(item?.SALESTYPE || "").trim().toUpperCase();
            const isDeduction =
              itemType === "EB" ||
              itemType === "RB" ||
              salesType.includes("RETURN") ||
              salesType.includes("EXCHANGE");

            const makeNegative = (val) => {
              const num = Number(val || 0);
              return isDeduction ? -Math.abs(num) : num;
            };

            return {
              ...item,
              isEB: isDeduction,
              qty: makeNegative(item?.qty),
              amount: makeNegative(item?.amount),
              DisAmt: makeNegative(item?.DisAmt),
              ACTRATE: makeNegative(item?.ACTRATE),
              COMM: makeNegative(item?.COMM),
              TOKEN: makeNegative(item?.TOKEN),
              GSTAMT: makeNegative(item?.GSTAMT),
              CGSTAMT: makeNegative(item?.CGSTAMT),
              SGSTAMT: makeNegative(item?.SGSTAMT),
              IGSTAMT: makeNegative(item?.IGSTAMT),
              BASICAMT: makeNegative(item?.BASICAMT),
              key: index + 1,
              serialNo: index + 1,
            };
          });
          setFilteredData(calculatedData);
          setTimeout(() => setTableVisible(true), 80);
        })
        .catch((error) => {
          console.error("Error fetching product wise sale details:", error);
        })
        .finally(() => setLoading(false));
    }
  }, [dates]);

  const columns = [
    {
      title: "S.No",
      dataIndex: "serialNo",
      width: 50,
      align: "center",
      key: "serialNo",
      className: "col-sno",
    },
    {
      title: "Inv No",
      dataIndex: "billno",
      key: "billno",
      align: "center",
      className: "col-tagno",
      render: (value) => <span className="tagBadge">{value}</span>,
    },
    {
      title: "Inv Date",
      dataIndex: "billdate",
      key: "billdate",
      align: "center",
      className: "col-date",
    },
    {
      title: "Brand/Design Code",
      dataIndex: "ITEMCODE",
      key: "ITEMCODE",
      align: "left",
    },
    {
      title: "Item Name",
      dataIndex: "itemname",
      key: "itemname",
      align: "left",
      className: "col-productname",
    },
    {
      title: "HSN Code",
      dataIndex: "HSNCODE",
      key: "HSNCODE",
      align: "right",
    },
    {
      title: "Product Size",
      dataIndex: "PRODSIZE",
      key: "PRODSIZE",
      align: "left",
    },
    {
      title: "Color",
      dataIndex: "COLOUR",
      key: "COLOUR",
      align: "left",
    },
    {
      title: "Fashion",
      dataIndex: "FITTING",
      key: "FITTING",
      align: "left",
    },
    {
      title: "Offer",
      dataIndex: "SLEVES",
      key: "SLEVES",
      align: "left",
    },
    {
      title: "Qty",
      dataIndex: "qty",
      key: "qty",
      align: "center",
      className: "col-pieces",
      render: (value) => <span className="piecesBadge">{value}</span>,
    },
    {
      title: "MRP",
      dataIndex: "rate",
      key: "rate",
      align: "right",
    },
    {
      title: "Dis.Amt",
      dataIndex: "DisAmt",
      key: "DisAmt",
      align: "right",
    },
    {
      title: "Cust.Price",
      dataIndex: "ACTRATE",
      key: "ACTRATE",
      align: "right",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      className: "col-netwt",
      render: (value) => <span className="wtBadge">{value}</span>,
    },
    {
      title: "SM Code",
      dataIndex: "SMCODE",
      key: "SMCODE",
      align: "left",
    },
    {
      title: "Comm.Amt",
      dataIndex: "COMM",
      key: "COMM",
      align: "right",
    },
    {
      title: "Token",
      dataIndex: "TOKEN",
      key: "TOKEN",
      align: "right",
    },
    {
      title: "GST",
      dataIndex: "GST",
      key: "GST",
      align: "right",
    },
    {
      title: "GST AMT",
      dataIndex: "GSTAMT",
      key: "GSTAMT",
      align: "right",
    },
    {
      title: "CGST",
      dataIndex: "CGSTRATE",
      key: "CGSTRATE",
      align: "right",
    },
    {
      title: "CGST AMT",
      dataIndex: "CGSTAMT",
      key: "CGSTAMT",
      align: "right",
    },
    {
      title: "SGST",
      dataIndex: "SGSTRATE",
      key: "SGSTRATE",
      align: "right",
    },
    {
      title: "SGST AMT",
      dataIndex: "SGSTAMT",
      key: "SGSTAMT",
      align: "right",
    },
    {
      title: "IGST",
      dataIndex: "IGSTRATE",
      key: "IGSTRATE",
      align: "right",
    },
    {
      title: "IGST AMT",
      dataIndex: "IGSTAMT",
      key: "IGSTAMT",
      align: "right",
    },
    {
      title: "Basic AMT",
      dataIndex: "BASICAMT",
      key: "BASICAMT",
      align: "right",
    },
    {
      title: "Brand Code",
      dataIndex: "BRANDCODE",
      key: "BRANDCODE",
      align: "left",
    },
  ];

  /* ── totals (blank values count as 0) ── */
  const totals = useMemo(() => {
    return filteredData.reduce(
      (acc, item) => {
        acc.qty += Number(item?.qty || 0);
        acc.amount += Number(item?.amount || 0);
        acc.comm += Number(item?.COMM || 0);
        acc.token += Number(item?.TOKEN || 0);
        acc.gstAmt += Number(item?.GSTAMT || 0);
        acc.basic += Number(item?.BASICAMT || 0);
        return acc;
      },
      { qty: 0, amount: 0, comm: 0, token: 0, gstAmt: 0, basic: 0 },
    );
  }, [filteredData]);

  /* rows shown in the table (dates pre-formatted, no total row) */
  const formattedData = useMemo(
    () =>
      filteredData.map((item, index) => ({
        ...item,
        serialNo: index + 1,
        billdate: item?.billdate
          ? dayjs(item.billdate).format("DD/MMM/YYYY")
          : "",
        rate: Number(item?.rate || 0).toFixed(2),
        DisAmt: Number(item?.DisAmt || 0).toFixed(2),
        ACTRATE: Number(item?.ACTRATE || 0).toFixed(2),
        amount: Number(item?.amount || 0).toFixed(2),
        COMM: Number(item?.COMM || 0).toFixed(2),
        TOKEN: Number(item?.TOKEN || 0).toFixed(2),
        GSTAMT: Number(item?.GSTAMT || 0).toFixed(2),
        CGSTAMT: Number(item?.CGSTAMT || 0).toFixed(2),
        SGSTAMT: Number(item?.SGSTAMT || 0).toFixed(2),
        IGSTAMT: Number(item?.IGSTAMT || 0).toFixed(2),
        BASICAMT: Number(item?.BASICAMT || 0).toFixed(2),
      })),
    [filteredData],
  );

  /* export data = rows + total row */
  const exportData = [
    ...formattedData,
    {
      serialNo: "Total",
      billno: "",
      billdate: "",
      ITEMCODE: "",
      itemname: "",
      qty: totals.qty,
      amount: totals.amount.toFixed(2),
      COMM: totals.comm,
      TOKEN: totals.token,
      GSTAMT: totals.gstAmt,
      BASICAMT: totals.basic.toFixed(2),
    },
  ];

  return (
    <div className={styles.page}>
      <InventoryPageHeader
        title="Product Wise Sale"
        subtitle="Point of Sale · Product Wise Sale"
        icon={<ShoppingOutlined style={{ color: "#fff", fontSize: 16 }} />}
        recordCount={filteredData.length}
        visible={filteredData.length > 0}
        stats={[
          {
            label: "Records",
            value: filteredData.length,
            color: "blue",
            decimals: 0,
          },
          { label: "Qty", value: totals.qty, color: "orange", decimals: 0 },
          {
            label: "Basic Amt",
            value: totals.basic,
            color: "amber",
            decimals: 2,
          },
          {
            label: "Amount",
            value: totals.amount,
            color: "green",
            decimals: 2,
          },
        ]}
      />

      <div className={styles.toolbar} style={{ flexWrap: "wrap", rowGap: 12 }}>
        <div
          className={styles.toolbarLeft}
          style={{ flexWrap: "wrap", rowGap: 12 }}
        >
          <div className="mainProductHighlight">
            <span className="mainProductIcon">
              <CalendarOutlined />
            </span>
            <div className="mainProductInner">
              <div className={styles.toolbarLabel}>Start Date</div>
              <DatePicker
                className="mainProductPicker"
                style={{ width: "100%" }}
                size="middle"
                value={dates[0]}
                onChange={(d) => setDates([d || dayjs(), dates[1]])}
                format="DD/MM/YYYY"
                allowClear={false}
                disabledDate={(current) =>
                  current && current > dayjs().endOf("day")
                }
              />
            </div>
          </div>

          <div className="mainProductHighlight">
            <span className="mainProductIcon">
              <CalendarOutlined />
            </span>
            <div className="mainProductInner">
              <div className={styles.toolbarLabel}>End Date</div>
              <DatePicker
                className="mainProductPicker"
                style={{ width: "100%" }}
                size="middle"
                value={dates[1]}
                onChange={(d) => setDates([dates[0], d || dayjs()])}
                format="DD/MM/YYYY"
                allowClear={false}
                disabledDate={(current) =>
                  current && current > dayjs().endOf("day")
                }
              />
            </div>
          </div>
        </div>

        <div className={styles.toolbarRight}>
          <PdfExcelPrint
            data={exportData}
            columns={columns}
            fileName="ProductWiseSale"
            disabled={formattedData.length === 0}
          />
        </div>
      </div>

      <InventoryTable
        columns={columns}
        dataSource={formattedData}
        rowKey="key"
        tableClassName="mr-table"
        rowClassName={(record) => (record?.isEB ? "ebRow" : "")}
        tableEnter={tableVisible}
        loading={loading}
        emptyText="No sale records found for the selected dates"
        primaryFields={primaryFields}
        secondaryFields={secondaryFields}
        summary={() => (
          <Table.Summary.Row style={{ background: "#f5f5f5", fontWeight: 600 }}>
            {columns.map((col, index) => {
              if (index === 0) {
                return (
                  <Table.Summary.Cell key={col.key}>Total</Table.Summary.Cell>
                );
              }
              const totalByKey = {
                qty: totals.qty,
                amount: totals.amount.toFixed(2),
                COMM: totals.comm.toFixed(2),
                TOKEN: totals.token.toFixed(2),
                GSTAMT: totals.gstAmt.toFixed(2),
                BASICAMT: totals.basic.toFixed(2),
              };
              if (col.key in totalByKey) {
                return (
                  <Table.Summary.Cell key={col.key} align="right">
                    {totalByKey[col.key]}
                  </Table.Summary.Cell>
                );
              }
              return <Table.Summary.Cell key={col.key} />;
            })}
          </Table.Summary.Row>
        )}
      />

      <style>{`
  @keyframes mainProductPulse {
    0%   { box-shadow: 0 0 0 0 rgba(24,144,255,0.30); }
    70%  { box-shadow: 0 0 0 8px rgba(24,144,255,0); }
    100% { box-shadow: 0 0 0 0 rgba(24,144,255,0); }
  }
  .mainProductHighlight {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 18px 8px 10px;
    border-radius: 14px;
    background: #eef6ff;
    border: 1.5px solid #65aefd;
    box-shadow: 0 0 0 1.5px #bcdcff inset;
    animation: mainProductPulse 2.4s infinite;
    min-width: 210px;
    flex-shrink: 0;
  }
  .mainProductIcon {
    width: 34px;
    height: 34px;
    border-radius: 9px;
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(24,144,255,0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: #1677ff;
    flex-shrink: 0;
  }
  .mainProductInner {
    display: flex;
    flex-direction: column;
    gap: 3px;
    flex: 1;
    min-width: 0;
  }
  .mainProductPicker.ant-picker {
    height: 34px !important;
    border-radius: 8px !important;
    background: #ffffff !important;
    border: 1px solid #d6e8ff !important;
    width: 100%;
  }
  .mainProductPicker.ant-picker:hover {
    border-color: #65aefd !important;
  }
  .mainProductPicker.ant-picker-focused {
    border-color: #1890ff !important;
    box-shadow: none !important;
  }
  .mainProductPicker .ant-picker-input > input {
    font-size: 13px;
    font-weight: 600;
    color: #1f2430;
  }
  .ant-table-thead > tr > th {
    white-space: nowrap !important;
  }
  /* ── Exchange Row (TYPE = 'EB') styling ── */
  .ebRow,
  .ebRow > td {
    background-color: #fff1f2 !important;
    color: #9f1239 !important;
  }
  .ebRow:hover > td {
    background-color: #ffe4e6 !important;
  }
  .ebRow .tagBadge,
  .ebRow .piecesBadge,
  .ebRow .wtBadge {
    background-color: #ffe4e6 !important;
    color: #9f1239 !important;
    border-color: #fecdd3 !important;
  }
`}</style>
    </div>
  );
};

export default ProductWiseSale;